import {
	equal,
	integer,
	min,
	required,
	string,
} from 'vue-mc/validation';
import Model from './Model';
import Collection from './Collection';
import PQueue from 'p-queue';
import moment from 'moment';

/**
 * Article model
 */
export class Article extends Model {

	// Default attributes that define the "empty" state.
	defaults() {
		return {
			id: null,
			url: '',
			reportId: null,
			projectId: null,
			title: '',
			author: '',
			date: '',
			image: null,
			images: [],
			issues: [],
			customData: null,
			categoryId: null,
			publicationName: '',
			publicationId: null,
			important: 0,
			socialStats: {
				facebook_like: 0,
				facebook_share: 0,
				facebook_total: 0,
				linkedin: 0,
				pinterest: 0,
				twitter: 0,
			},
			added: null,
			justAdded: false,
			isDeleted: false,
			alternatives: {
				date: [],
				title: [],
				author: [],
				publicationName: [],
				summary: [],
			},
			reprints: [],
			completions: [],
		};
	}

	// Attribute mutations.
	mutations() {
		return {
			id: id => Number(id) || null,
			url: String,
			important: important => this.mutateImportant(important) || 100,
			isDeleted: Boolean,
			publicationName: name => name || '',
			publicationId: id => Number(id) || null,
		};
	}

	// Attribute validation
	validation() {
		return {
			id: integer.and(min(1)).or(equal(null)),
			projectId: required.and(integer),
			reportId: required.and(integer),
			url: string.and(required),
			publicationId: integer.or(equal(null)),
		};
	}

	// Route configuration
	routes() {
		return {
			create: '/articles',
			fetch: '/articles/{id}',
			patch: '/articles/{id}',
			delete: '/articles/{id}',
			refetch: 'articles/{id}/refetch-data',
			updateSocial: 'articles/{id}/refetch-social-media-stats',
			uploadImage: 'articles/{id}/upload-image',
			translate: 'articles/{id}/translate',
			addReprints: 'articles/{id}/add-reprints',
			completion: 'articles/{id}/completion',
		};
	}

	options() {
		return {
			patch: true,
			methods: {
				updateSocial: 'GET',
				refetch: 'GET',
				uploadImage: 'POST',
				translate: 'GET',
				addReprints: 'POST',
				completion: 'POST',
			},
		};
	}

	/**
	 * Method for custom routes
	 * @returns {Promise} Result of generic article request
	 */
	customRequest(actionName, data = null) {
		this.$store.set('events/articlesLoading', { changing: true, type: actionName });

		const method = this.getOption(`methods.${actionName}`);
		const route = this.getRoute(actionName);
		const url = this.getURL(route, this.getRouteParameters());

		const p = new Promise((resolve, reject) => {
			this.loading = true;
			this.getRequest({ method, url, headers: { Authorization: '' }, data }).send().then((resp) => {
				this.set(resp.response.data);
				this.sync(); // sync active state and saved state, so store has this new data
				resolve();
			}).catch((e) => {
				reject(e);
			}).finally(() => {
				this.loading = false;
			});
		});

		p.finally(() => {
			this.$store.set('events/articlesLoading', { changing: false });
		});

		return p;
	}

	/**
	 * Action for refetching article and dealing with events updating
	 */
	refetchArticle() {
		this.customRequest('refetch')
			.then(() => {
				this.$store.set('notification', {
					type: 'success',
					text: 'Article has been refetched.',
				});
			})
			.catch((e) => {
				dump(e);

				this.$store.set('notification', {
					type: 'error',
					text: 'Failed to refetch article',
				});
			});
	}

	/**
	 * Action for translating article and dealing with events updating
	 */
	translateArticle() {
		this.customRequest('translate')
			.then(() => {
				this.$store.set('notification', {
					type: 'success',
					text: 'Article has been translated.',
				});
			})
			.catch((e) => {
				dump(e);

				this.$store.set('notification', {
					type: 'error',
					text: 'Failed to translate article',
				});
			});
	}

	/**
	 * Method for custom route 'updateSocial'
	 * @returns {Promise} Result of updating social stats
	 */
	updateSocial() {
		const method = this.getOption('methods.updateSocial');
		const route = this.getRoute('updateSocial');
		const url = this.getURL(route, this.getRouteParameters());

		return new Promise((resolve, reject) => {
			this.loading = true;
			this.getRequest({ method, url, headers: { Authorization: '' } })
				.send()
				.then((response) => {
					this.socialStats = response.response.data.socialStats;
					resolve();
				}).catch((error) => {
					reject(error);
				})
				.finally(() => {
					this.loading = false;
				});
		});
	}

	/**
	 * Action for updating social stats and dealing with events updating
	 */
	updateSocialStats() {

		this.$store.set('events/articlesLoading', { changing: true, type: 'social-stats'});

		this.updateSocial()
			.then(() => {

				this.$store.set('notification', {
					type: 'success',
					text: 'Social stats have been updated.',
				});

			}).catch((e) => {

				dump(e);

				this.$store.set('notification', {
					type: 'error',
					text: 'Failed to update social stats',
				});

			}).finally(() => {
				this.$store.set('events/articlesLoading', { changing: false });
			});
	}

	uploadImage(uri) {
		const method = this.getOption('methods.uploadImage');
		const route = this.getRoute('uploadImage');
		const url = this.getURL(route, this.getRouteParameters());

		return new Promise((resolve, reject) => {
			this.loading = true;
			this.getRequest({ method,
				url,
				headers: { Authorization: '' },
				data: { uri },
			})
				.send()
				.then(({ response }) => {
					this.image = response.data.image;
					this.images = response.data.images;
					resolve(response);
				}).catch((error) => {
					reject(error);
				})
				.finally(() => {
					this.loading = false;
				});
		});
	}

	/**
	 * Action for restoring deleted article (setting isDeleted to false)
	 */
	restore() {

		this.$store.set('events/articlesLoading', { changing: true, type: 'restore'});

		this.isDeleted = false;
		this.save().then(() => {

			// Remove from the store
			this.$store.commit('REMOVE_DELETED_ARTICLE_BY_ID', this.id);

			/**
			 * @todo Find better way to update articles in store after restored
			 */
			var articles = new Articles();
			articles.set('id', this.reportId);
			articles.fetch({
				params: {
					isDeleted: '0',
				}
			}).then(() => {
				this.$store.set('articles', articles.models);
			});

			this.$store.set('notification', {
				type: 'success',
				text: `"${this.titleText}" has been restored.`,
			});

		}).catch((e) => {

			dump(e);

			this.$store.set('notification', {
				type: 'error',
				text: `Failed to restore "${this.titleText}".`,
			});

		}).finally(() => {
			this.$store.set('events/articlesLoading', { changing: false });
		});
	}

	/**
	 * Add reprints to article
	 * @param {string[]} urls Array of URLs to add as reprints
	 */
	addReprints(urls) {
		this.customRequest('addReprints', { urls })
			.then(() => {
				this.$store.set('notification', {
					type: 'success',
					text: 'Reprints added.',
				});
			})
			.catch((e) => {
				dump(e);

				this.$store.set('notification', {
					type: 'error',
					text: 'Failed to add reprints',
				});
			});
	}
	
	generateCompletions(customDataName) {
		const customDataId = customDataName.replace('custom_data_', '');
		return this.customRequest('completion', { customDataId })
			.then(() => {
				
			})
			.catch((e) => {
				dump(e);

				this.$store.set('notification', {
					type: 'error',
					text: 'Failed to generate completions',
				});
			});
	}
	
	justAddedTimeout() {
		setTimeout(() => {
			this.justAdded = false;
		}, 300000);
	}

	mutateImportant(important) {
		return (important === 0 ? null : important);
	}

	/**
	 *
	 * @param {string} name One of available alternatives name
	 * @returns {boolean}
	 */
	hasAlternatives(name) {
		return this.alternatives[name] && this.alternatives[name].length > 0;
	}

	get titleText() {
		return (this.title || '(no title article)');
	}

	get facebookShares() {
		if (!this.socialStats) {
			return 0;
		}
		if (!this.socialStats.facebook_total) {
			return 0;
		}
		return this.socialStats.facebook_total;
	}

	get twitterShares() {
		if (!this.socialStats) {
			return 0;
		}
		const shares = this.socialStats.twitter;
		if (!shares) {
			return 0;
		}
		if (shares >= 99) {
			return '99+';
		}
		return shares;
	}

	get errors() {
		const obj = this.issues.errors;
		
		if (!obj) {
			return {};
		}
			
		// Author error is a user preference
		const skills = JSON.parse(localStorage.getItem('skills'));
		if (obj.author && !(skills && skills.items.find(skill => skill === 'author_issue'))) {
			delete obj.author;
		}

		return obj;
	}
	
	get errorsList() {
		const obj = this.errors;
		let list = [];
		for (const prop in obj) {
			list = list.concat(obj[prop]);
		}

		//console.log(list);
		
		return list;
	}
	
	get errorsSum() {
		let list = this.errorsList;
		return list.length;
	}

	get warnings() {
		const warnings = [];
		if (!this.issues) {
			return warnings;
		}
		// eslint-disable-next-line
		for (const property in this.issues.warnings) {
			// eslint-disable-next-line
			for (const item in this.issues.warnings[property]) {
				warnings.push(this.issues.warnings[property][item]);
			}
		}
		return warnings;
	}

	get flags() {
		if (!this.justAdded) {
			return [];
		}
		return [{ text: 'Recently Added', color: 'secondary' }];
	}

	get categoryModel() {
		return this.relation('categories', category => category.id === this.categoryId);
	}

	get categoryName() {
		const category = this.categoryModel;
		if (category === null) {
			return '';
		}
		return category.name;
	}

	get reportModel() {
		return this.relation('reports/all', report => report.id === this.reportId);
	}

	get userModel() {
		return this.relation('users/users', user => user.id === this.userId);
	}

	/**
	 * @returns {Publication} Related publication model
	 */
	get publicationModel() {
		return this.relation('reportPublications', publication => publication.id === this.publicationId);
	}

	// get justAdded() {
	// 	if (!this.added) {
	// 		return true;
	// 	}
	// 	const date = new Date(this.added);
	// 	const currentDate = new Date();
	// 	currentDate.setMinutes(currentDate.getMinutes() - 1);
	// 	if (currentDate < date) {
	// 		return true;
	// 	}
	// 	return false;
	// }

	get timeAddedAgo() {
		return moment(this.added).fromNow();
	}

	get userName() {
		const user = this.userModel;
		if (!user) {
			return 'User';
		}

		return user.realName;
	}
}

/**
 * Articles collection
 */
export class Articles extends Collection {

	model() {
		return Article;
	}

	routes() {
		return {
			fetch: 'articles?reportId={id}',
			// Custom routes
			// @todo Implement on backend
			// moveArticles: '',
			// copyArticles: '',
			// deleteArticles: '',
		};
	}

	// @todo Implement on backend
	// options() {
	// 	return {
	// 		patch: true,
	// 		methods: {
	// 			copyArticles: 'POST',
	// 			moveArticles: 'POST',
	// 			deleteArticles: 'POST',
	// 		},
	// 	};
	// }


	/** @todo Finish for 'copyArticles', 'moveArticles', deleteArticles' */

	/**
	 * Method for custom route 'moveArticles'
	 * @param {number} categoryId Selected category ID
	 * @returns {Promise} Result of updating articles as a promise
	 */
	// moveArticles(categoryId) {
	// 	const method = this.getOption('methods.moveArticles');
	// 	const route = this.getRoute('moveArticles');
	// 	const url = this.getURL(route, this.getRouteParameters());
	// 	const data = {
	// 		articleIds: this.ids,
	// 		attributes: {
	// 			category: categoryId,
	// 		},
	// 	};

	// 	return new Promise((resolve, reject) => {
	// 		this.getRequest({ method, url, data, headers: { Authorization: '' } })
	// 			.send()
	// 			.then(() => {
	// 				resolve();
	// 			})
	// 			.catch((error) => {
	// 				reject(error);
	// 			});
	// 	});
	// }

	/**
	 * @returns {Array} Array of articles ids in collection
	 */
	get ids() {
		return this.map((article) => article.id);
	}

	/**
	 * @returns {string} Article or aricles word, depends on how many articles in collection
	 */
	get singleOrPluralArticleWord() {
		return 'article' + (this.models.length > 1 ? 's' : '');
	}

	/**
	 * Sets each model's loading in collection to a value
	 * @param {boolean} value
	 */
	setModelsLoading(value) {
		this.each((article) => { article.loading = value });
	}

	/**
	 * Moves selected articles to another report
	 * @todo Update to articles end-point
	 * @param {Report} report Current report to which this article related
	 * @param {number} categoryId Selected category ID
	 */
	move(report, categoryId) {

		this.$store.set('events/articlesLoading', { changing: true, type: 'move'});
		this.setModelsLoading(true);

		const newCategory = (categoryId === 0 ? null : categoryId);

		report.moveArticles(this.ids, newCategory)
			.then(() => {

				// Remove from storage to update view
				this.each((article) => {
					this.$store.commit('REMOVE_ARTICLE_BY_ID', article.id);
				});

				this.$store.set('notification', {
					type: 'success',
					text: `Selected ${this.singleOrPluralArticleWord} moved to "${report.displayName}".`,
					to: { name: 'ArticlesList', params: { reportId: report.id } },
				});

			}).catch((e) => {

				dump(e);

				this.$store.set('notification', {
					type: 'error',
					text: `Selected ${this.singleOrPluralArticleWord} failed to move.`,
				});
			}).finally(() => {
				this.$store.set('events/articlesLoading', { changing: false });
				this.setModelsLoading(true);
			});
	}

	/**
	 * Copies selected articles to another report
	 * @todo Update to articles end-point
	 * @param {Report} report Current report to which this article related
	 * @param {number} categoryId Selected category ID
	 */
	copy(report, categoryId) {

		this.$store.set('events/articlesLoading', { changing: true, type: 'copy'});
		this.setModelsLoading(true);

		const newCategory = (categoryId === 0 ? null : categoryId);

		report.copyArticles(this.ids, newCategory)
			.then(() => {

				/**
				 * Add to storage created (copied) article to update view
				 * @todo Not ideal, because we refetch all articles (/v2/reports/{id}/batch-copy doesn't return anything)
				 * @todo Will be updated after end-points done
				 */
				this.set('id', report.id);
				this.fetch()
					.then(() => {
						this.$store.set('articles', this.models);
					});

				this.$store.set('notification', {
					type: 'success',
					text: `Selected ${this.singleOrPluralArticleWord} copied to "${report.displayName}".`,
					to: { name: 'ArticlesList', params: { reportId: report.id } },
				});

			}).catch((e) => {

				dump(e);

				this.$store.set('notification', {
					type: 'error',
					text: `Selected ${this.singleOrPluralArticleWord} failed to copy.`,
				});

			}).finally(() => {
				this.$store.set('events/articlesLoading', { changing: false });
				this.setModelsLoading(true);
			});
	}

	/**
	 * Deletes selected articles
	 * @todo Update to articles end-point
	 * @param {Report} report Current report to which this article related
	 */
	deleteArticles(report) {

		this.$store.set('events/articlesLoading', { changing: true, type: 'delete'});

		// Setting each article in collection to be in loading state
		this.setModelsLoading(true);

		report.deleteArticles(this.ids).then(() => {

			// Remove from storage to update view
			this.each((article) => {
				this.$store.commit('REMOVE_ARTICLE_BY_ID', article.id);
			});

			/**
			 * @todo Find better way to update deleted articles in store after restored
			 */
			var articles = new Articles();
			articles.set('id', report.id);
			articles.fetch({
				params: {
					isDeleted: '1',
				}
			}).then(() => {
				this.$store.set('deletedArticles', articles.models);
			});

			this.$store.set('notification', {
				type: 'success',
				text: `Selected ${this.singleOrPluralArticleWord} deleted.`,
			});

		}).catch((e) => {

			dump(e);

			this.$store.set('notification', {
				type: 'error',
				text: `Selected ${this.singleOrPluralArticleWord} failed to delete.`,
			});

		}).finally(() => {
			this.$store.set('events/articlesLoading', { changing: false });
			this.setModelsLoading(false);
		});
	}

	/**
	 * Changes category for selected articles
	 * @param {number} categoryId Selected category ID
	 */
	changeCategory(categoryId) {

		var queue = new PQueue({ concurrency: 2 });

		var hasError = false;
		this.$store.set('events/articlesLoading', { changing: true, type: 'change-category'});
		this.setModelsLoading(true);

		this.each((article) => {

			if (categoryId !== 0) {
				article.categoryId = categoryId;
			}

			queue.add(() => article.save().catch(() => {
				hasError = true;
			}));

		});

		queue.onIdle().then(() => {

			this.$store.set('events/articlesLoading', { changing: false });
			this.setModelsLoading(true);

			if (hasError) {
				this.$store.set('notification', {
					type: 'error',
					text: `Selected ${this.singleOrPluralArticleWord} failed to change categories.`,
				});
			}
			else {
				this.$store.set('notification', {
					type: 'success',
					text: `Selected ${this.singleOrPluralArticleWord} categories changed.`,
				});
			}
		});
	}
	
	/**
	 * Refetch selected articles stats
	 * @param {Report} report Current report to which this article related
	 */
	refetchArticlesStats(report) {
		
		this.$store.set('events/articlesLoading', { changing: true, type: 'refetch-stats'});

		// Setting each article in collection to be in loading state
		this.setModelsLoading(true);
		
		report.refetchArticlesStats(this.ids).then(() => {
			this.$store.set('notification', {
				type: 'success',
				text: `Stats for selected ${this.singleOrPluralArticleWord} refetched.`,
			});
		}).catch((e) => {
			dump(e);
			this.$store.set('notification', {
				type: 'error',
				text: `Stats for selected ${this.singleOrPluralArticleWord} failed to refetch.`,
			});
		}).finally(() => {
			this.$store.set('events/articlesLoading', { changing: false });
			this.setModelsLoading(false);
		});
	}

}

export class ArticlesSearch extends Collection {

	model() {
		return Article;
	}

	defaults() {
		return {
			projectIds: [],
			reportIds: [],
			userIds: [],
			categoryIds: [],
			isDeleted: false,
			publisherIds: [],
			search: '',
			params: '/search',
		};
	}

	routes() {
		return {
			fetch: 'articles{params}',
		};
	}

}
